import React, { useState, useEffect } from 'react';
import moment from 'moment';
import i18next from '../../i18n.js';
import { listTypes } from '../../constants.js';
import { loadListData } from '../helpers/HelperFunctions.js';
import TripDocuments from './TripDocuments.js';

// Спільний findField не заходить у formgroup, а мета поїздки лежить саме там,
// тому для довідників цієї картки потрібен пошук по всій глибині форми
const findFieldDeep = (fields, name) => {
    for (const field of fields ?? []) {
        if (field.name === name) {
            return field;
        }
        const found = findFieldDeep(field.fields, name);
        if (found) {
            return found;
        }
    }
    return null;
}

// Значення, яких немає (наприклад транспорт, коли маршрут ще формує Travel-менеджер)
const emptyValue = '—';

// Картка погодження відрядження керівником. За дизайном показує лише те, що потрібне
// для рішення: кого відряджають, коли, куди, чим і навіщо. Нічого не редагує.
const TripTaskForm = (props) => {
    // showDocuments — у частині завдань відряджуваному треба бачити ще й файли заявки
    const { properties, fields, showDocuments } = props;
    const [secondedName, setSecondedName] = useState('');

    // у властивостях документа лежить лише nodeRef співробітника, тому ПІБ доводиться
    // тягнути тим самим списком користувачів, що й UserInput у режимі читання
    const secondedNodeRef = properties['assoc:sx:btSeconded_added'];
    useEffect(() => {
        if (!secondedNodeRef) {
            setSecondedName('');
            return;
        }
        let canceled = false;
        loadListData(listTypes.Users, [{ name: 'Id', values: [secondedNodeRef.split('/').pop()] }], 0, 200)
            .then(data => {
                if (canceled) return;
                setSecondedName(data?.items?.[0]?.fields?.find(field => field.name === 'FullName')?.value ?? '');
            });
        return () => { canceled = true; };
    }, [secondedNodeRef]);

    const dictLabel = (fieldName, value) =>
        findFieldDeep(fields, fieldName)?.values?.data?.find(item => item.value === value)?.label;

    const fmt = (value) => value ? moment(value.toString().split('T')[0]).format('DD.MM.YYYY') : '';

    const multi = !!properties['sx:btMultipleRoutes'];
    const routes = properties['routeForm'] ?? [];
    const backForm = (properties['goingBackForm'] ?? [])[0];
    const departureCity = properties['sx:btMainPlace'] ? properties['sx:btCityReturn'] : properties['sx:btReasonWorkPlace'];

    const travel = (form) => (form?.properties?.['travelForm'] ?? [])[0]?.properties ?? {};
    const routeCity = (form) => form?.properties?.['sx:pbtCity'] || form?.properties?.['sx:pbtLocality'] || '';
    const routeTo = (form) => travel(form)['sx:tfPointArrival'] || routeCity(form) || '';
    const transitForms = (form) => form?.properties?.['transitForm'] ?? [];

    // Ланцюжок міст: точка відправлення, далі кожен маршрут зі своїми транзитними
    // зупинками. Повернення в місто виїзду не дописуємо — воно й так очевидне, а рядок
    // від цього став би вдвічі довшим
    const routePlaces = () => {
        const places = [travel(routes[0])['sx:tfPoint'] || departureCity];
        routes.forEach(route => {
            transitForms(route).forEach(transit => places.push(transit.properties['sx:pbtCity']));
            places.push(routeTo(route));
        });
        // сусідні однакові міста злипаються в одне: кінець маршруту і початок наступного
        return places.filter((place, index) => place && place !== places[index - 1]);
    }

    // Один і той самий транспорт на всі маршрути задається одним полем, інакше збираємо
    // всі використані види: маршрути, транзитні зупинки і дорога назад
    const transportMethods = () => {
        if (multi && properties['sx:btSameTransportForRoutes']) {
            return [properties['methodTransportationForAll']];
        }
        const methods = [];
        routes.forEach(route => {
            methods.push(route.properties['sx:pbtMethodTransportation']);
            transitForms(route).forEach(transit => methods.push(transit.properties['sx:pbtMethodTransportation']));
            if (!multi && !route.properties['sx:pbtSameTransportBack']) {
                methods.push(route.properties['sx:pbtBackMethodTransportation']);
            }
        });
        if (backForm) {
            methods.push(backForm.properties['sx:pbtMethodTransportation']);
        }
        return methods;
    }

    const distinct = (values) => [...new Set(values.filter(Boolean))];

    const transportText = distinct(transportMethods())
        .map(method => dictLabel('sx:pbtMethodTransportation', method) ?? dictLabel('methodTransportationForAll', method) ?? method)
        .join(', ');

    const purposeText = distinct(routes.map(route =>
        dictLabel('sx:pbtTripGoal', route.properties['sx:pbtTripGoal']) || dictLabel('sx:pbtTripGoalForeign', route.properties['sx:pbtTripGoalForeign'])))
        .join(', ');

    const periodText = [fmt(properties['sx:btDateStart']), fmt(properties['sx:btDateEnd'])].filter(Boolean).join(' – ');

    const rows = [
        { key: 'seconded', label: i18next.t('document.tripApprovalSeconded'), value: secondedName },
        { key: 'period', label: i18next.t('document.tripApprovalPeriod'), value: periodText },
        { key: 'route', label: i18next.t('document.tripApprovalRoute'), value: routePlaces().join(' → ') },
        { key: 'transport', label: i18next.t('document.tripApprovalTransport'), value: transportText },
        { key: 'purpose', label: i18next.t('document.tripApprovalPurpose'), value: purposeText }
    ];

    return (<div className="trip-approval">
        {/* заголовок — назва поточного етапу заявки, бо форма спільна для різних завдань відрядження */}
        <h2 className="trip-approval-title">{properties['sx:dfState_label']}</h2>
        <div className="trip-approval-card">
            {rows.map(row => <div className="trip-approval-row" key={row.key}>
                <div className="trip-approval-row-label">{row.label}</div>
                <div className="trip-approval-row-value">{row.value || emptyValue}</div>
            </div>)}
        </div>
        {showDocuments && <TripDocuments properties={properties} />}
    </div>);
}

export default TripTaskForm;
